type Callback = () => void;

class GlobalResizeObserver {
  private byEl = new Map<Element, Map<Callback, number>>();
  private observer = new ResizeObserver((entries) => {
    const callbacksToNotify = new Set<Callback>();
    for (const entry of entries) {
      const targetCallbacks = this.byEl.get(entry.target);
      if (!targetCallbacks) continue;
      for (const callback of targetCallbacks.keys()) {
        callbacksToNotify.add(callback);
      }
    }
    for (const callback of callbacksToNotify) {
      callback();
    }
  });

  /**
   * Adds observer, returns a cleanup function.
   *
   * If the same function (by refence) is added multiple times, it will only be called once.
   **/
  observe(el: Element, callback: Callback): () => void {
    let callbacks = this.byEl.get(el);
    if (!callbacks) {
      callbacks = new Map();
      this.byEl.set(el, callbacks);
      this.observer.observe(el);
    }
    callbacks.set(callback, (callbacks.get(callback) || 0) + 1);
    return () => this.unobserve(el, callback);
  }

  unobserve(el: Element, callback: Callback) {
    const callbacks = this.byEl.get(el);
    if (!callbacks) {
      return;
    }
    const count = (callbacks.get(callback) || 0) - 1;
    if (count > 0) {
      callbacks.set(callback, count);
      return;
    }
    callbacks.delete(callback);
    if (callbacks.size === 0) {
      this.observer?.unobserve(el);
      this.byEl.delete(el);
    }
  }
}

export const globalResizeObserver = new GlobalResizeObserver();
